import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PhoneOff, Mic, Loader2, Volume2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CALL_STRINGS = {
  en: {
    title: 'Voice call with Kasara',
    connecting: 'Connecting…',
    listening: 'Listening — just speak',
    thinking: 'Thinking…',
    speaking: 'Kasara is speaking',
    end: 'End call',
    interrupt: 'Tap to interrupt',
    micError: 'Microphone unavailable. Please allow mic access and try again.'
  },
  fr: {
    title: 'Appel vocal avec Kasara',
    connecting: 'Connexion…',
    listening: 'Je vous écoute — parlez',
    thinking: 'Réflexion…',
    speaking: 'Kasara vous répond',
    end: "Terminer l'appel",
    interrupt: 'Touchez pour interrompre',
    micError: "Microphone indisponible. Autorisez l'accès au micro puis réessayez."
  }
};

const SILENCE_MS = 1400;
const MAX_SEGMENT_MS = 30000;
const RMS_THRESHOLD = 0.022;

const blobToB64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(r.result.split(',')[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

const readSse = async (res, onDelta) => {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const evs = buf.split('\n\n');
    buf = evs.pop();
    for (const ev of evs) {
      if (!ev.startsWith('data: ')) continue;
      const p = ev.slice(6);
      if (p === '[DONE]') continue;
      try { const { delta } = JSON.parse(p); if (delta) onDelta(delta); } catch { /* skip */ }
    }
  }
};

export const VoiceCallOverlay = ({ lang, sessionId, onExchange, onClose }) => {
  const s = CALL_STRINGS[lang] || CALL_STRINGS.en;
  const [phase, setPhase] = useState('connecting'); // connecting|listening|thinking|speaking|error
  const [lastUser, setLastUser] = useState('');
  const [lastBot, setLastBot] = useState('');
  const aliveRef = useRef(true);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);

  const cleanup = useCallback(() => {
    aliveRef.current = false;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (ctxRef.current) { ctxRef.current.close().catch(() => {}); ctxRef.current = null; }
  }, []);

  const recordSegment = useCallback((stream) => new Promise((resolve) => {
    const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    const rec = new MediaRecorder(stream, { mimeType: mime });
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    rec.onstop = () => resolve({ blob: new Blob(chunks, { type: mime }), mime });

    const ctx = ctxRef.current;
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    const data = new Float32Array(analyser.fftSize);

    let spoke = false;
    let silentSince = null;
    const started = Date.now();
    const tick = setInterval(() => {
      if (!aliveRef.current || rec.state !== 'recording') { clearInterval(tick); src.disconnect(); return; }
      analyser.getFloatTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length);
      const now = Date.now();
      if (rms > RMS_THRESHOLD) { spoke = true; silentSince = null; }
      else if (spoke) {
        if (!silentSince) silentSince = now;
        else if (now - silentSince > SILENCE_MS) { clearInterval(tick); src.disconnect(); rec.stop(); return; }
      }
      if (now - started > MAX_SEGMENT_MS) { clearInterval(tick); src.disconnect(); rec.stop(); }
    }, 80);
    rec.start();
  }), []);

  const loop = useCallback(async (stream) => {
    while (aliveRef.current) {
      setPhase('listening');
      const { blob, mime } = await recordSegment(stream);
      if (!aliveRef.current) return;
      if (blob.size < 3000) continue; // no real speech captured
      setPhase('thinking');
      try {
        const b64 = await blobToB64(blob);
        const sttRes = await fetch(`${API}/api/voice/stt`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audio_base64: b64, mime, lang })
        });
        if (!sttRes.ok) continue;
        const { text } = await sttRes.json();
        const clean = (text || '').trim();
        if (!clean || !aliveRef.current) continue;
        setLastUser(clean);
        setLastBot('');
        let reply = '';
        const chatRes = await fetch(`${API}/api/chat`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, message: clean, lang })
        });
        if (!chatRes.ok || !chatRes.body) continue;
        await readSse(chatRes, (d) => { reply += d; setLastBot(r => r + d); });
        if (!aliveRef.current || !reply.trim()) continue;
        onExchange && onExchange(clean, reply);
        const ttsRes = await fetch(`${API}/api/voice/tts`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: reply.slice(0, 1500), lang })
        });
        if (!ttsRes.ok || !aliveRef.current) continue;
        const { audio } = await ttsRes.json();
        setPhase('speaking');
        await new Promise((resolve) => {
          const el = new Audio(`data:audio/mp3;base64,${audio}`);
          audioRef.current = el;
          el.onended = resolve;
          el.onerror = resolve;
          el.play().catch(resolve);
        });
        audioRef.current = null;
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.debug('Voice call turn failed, resuming listening:', err);
      }
    }
  }, [lang, sessionId, onExchange, recordSegment]);

  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!aliveRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        loop(stream);
      } catch {
        setPhase('error');
      }
    })();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interrupt = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.onended?.(); }
  };

  const statusText = { connecting: s.connecting, listening: s.listening, thinking: s.thinking, speaking: s.speaking, error: s.micError }[phase];
  const ringCls = {
    listening: 'border-[#16A34A] shadow-[0_0_30px_rgba(22,163,74,0.5)] animate-pulse',
    thinking: 'border-[#F29B3D] shadow-[0_0_30px_rgba(242,155,61,0.45)]',
    speaking: 'border-[#F29B3D] shadow-[0_0_40px_rgba(242,155,61,0.7)] animate-pulse',
    connecting: 'border-white/20',
    error: 'border-red-500'
  }[phase];

  return createPortal(
    <div data-testid="voice-call-overlay" data-no-translate className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0B0524]/80 backdrop-blur-md px-4">
      <div className="w-full max-w-[400px] rounded-3xl bg-[#120836] border border-white/10 p-8 text-center shadow-2xl">
        <p className="text-[#93A5BC] text-xs font-bold tracking-[2px] uppercase mb-6">{s.title}</p>
        <button type="button" onClick={phase === 'speaking' ? interrupt : undefined} className="relative mx-auto block" title={phase === 'speaking' ? s.interrupt : undefined}>
          <img src="/kasara-avatar.jpg" alt="Kasara" className={`w-28 h-28 rounded-full object-cover mx-auto border-4 transition-all duration-300 ${ringCls}`} />
          <span className="absolute -bottom-1 right-1 w-9 h-9 rounded-full bg-[#1E1050] border border-white/15 flex items-center justify-center text-white">
            {phase === 'listening' && <Mic className="w-4 h-4 text-[#16A34A]" />}
            {phase === 'thinking' && <Loader2 className="w-4 h-4 animate-spin text-[#F29B3D]" />}
            {phase === 'speaking' && <Volume2 className="w-4 h-4 text-[#F29B3D]" />}
            {(phase === 'connecting' || phase === 'error') && <Mic className="w-4 h-4 text-[#93A5BC]" />}
          </span>
        </button>
        <p data-testid="voice-call-status" className={`mt-5 text-sm font-semibold ${phase === 'error' ? 'text-red-400' : 'text-white'}`}>{statusText}</p>
        {phase === 'speaking' && <p className="text-[#93A5BC] text-[11px] mt-1">{s.interrupt}</p>}

        <div className="mt-6 min-h-[72px] max-h-[160px] overflow-y-auto text-left space-y-2 custom-scrollbar">
          {lastUser && <p className="text-[#93A5BC] text-xs leading-relaxed"><span className="text-[#F29B3D] font-bold">You · </span>{lastUser}</p>}
          {lastBot && <p className="text-white/90 text-xs leading-relaxed"><span className="text-[#F29B3D] font-bold">Kasara · </span>{lastBot}</p>}
        </div>

        <button
          type="button"
          data-testid="voice-call-end"
          onClick={() => { cleanup(); onClose(); }}
          className="mt-7 inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-bold px-6 py-3 rounded-full transition-colors"
        >
          <PhoneOff className="w-4 h-4" /> {s.end}
        </button>
      </div>
    </div>,
    document.body
  );
};
